'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ChevronRight, ChevronLeft, CheckCircle2, AlertCircle, BarChart3, Target, Lightbulb, ShieldCheck, BookOpen, Layers, Award, ArrowRight, Quote, PenLine, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { authenticatedApiClient } from '@/lib/api/client';
import { useSession } from '@/lib/auth-client';
import { toast } from 'sonner';

// --- Types ---

interface Answers {
  // Step 1
  landscapeConcern?: string;
  // Step 2
  pitchLeadsWith?: string;
  categoryArgumentRating?: string;
  // Step 3
  declineRisks?: string[];
  // Step 4
  proofPoints?: string[];
  strongestProofPoint?: string;
  // Three Things step
  threeThingsConfidence?: string[];
  // Step 5
  pitchDeckStatus?: string;
  // Step 6
  confidentParts?: string[];
  needsWorkParts?: string[];
  // Buyer Excitement step
  excitedCategories?: string[];
  // Market Bets step
  marketBetsRelevance?: string;
  // Step 9
  pillarCategoryLogic?: string;
  pillarCommercialProof?: string;
  pillarPitchPrecision?: string;
  pillarMarginArchitecture?: string;
  pillarOperationalReadiness?: string;
}

// --- Prompt Section Wrapper ---

function PromptSection(t0) {
  const $ = _c(11);
  const {
    title,
    description,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-2 mb-3"><PenLine className="w-4 h-4 text-indigo-500" /><span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Your Turn</span></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== description) {
    t3 = description && <p className="text-sm text-gray-500 mb-3">{description}</p>;
    $[3] = description;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children) {
    t4 = <div>{children}</div>;
    $[5] = children;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div className="border-l-[3px] border-indigo-500 bg-indigo-50/60 rounded-lg p-5 mt-6">{t1}{t2}{t3}{t4}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

// --- Component ---

interface AUReadinessReportAssessmentProps {
  /** Whether to show the step selector navigation pills. Hidden on mobile by default. */
  showStepSelector?: boolean;
}
export function AUReadinessReportAssessment(t0) {
  const $ = _c(135);
  const {
    showStepSelector: t1
  } = t0 === undefined ? {} : t0;
  const showStepSelector = t1 === undefined ? false : t1;
  const [currentStep, setCurrentStep] = useState(0);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [answers, setAnswers] = useState(t2);
  const [showResult, setShowResult] = useState(false);
  const wizardRef = useRef(null);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function scrollToTop() {
      const el = wizardRef.current;
      if (el) {
        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - 80
        });
      }
      if (typeof window !== "undefined" && window.self !== window.top) {
        window.parent.postMessage({
          type: "embed-scroll-top"
        }, "*");
      }
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const scrollToTop = t3;
  let t4;
  if ($[2] !== answers) {
    t4 = function isStepComplete(step) {
      switch (step) {
        case 0:
          {
            return true;
          }
        case 1:
          {
            return true;
          }
        case 2:
          {
            return !!answers.landscapeConcern;
          }
        case 3:
          {
            return !!answers.pitchLeadsWith && !!answers.categoryArgumentRating;
          }
        case 4:
          {
            return true;
          }
        case 5:
          {
            return (answers.proofPoints?.length ?? 0) > 0;
          }
        case 6:
          {
            return true;
          }
        case 7:
          {
            return !!answers.pitchDeckStatus;
          }
        case 8:
          {
            return true;
          }
        case 9:
          {
            return (answers.excitedCategories?.length ?? 0) > 0;
          }
        case 10:
          {
            return !!answers.marketBetsRelevance;
          }
        case 11:
          {
            return !!answers.pillarCategoryLogic && !!answers.pillarCommercialProof && !!answers.pillarPitchPrecision && !!answers.pillarMarginArchitecture && !!answers.pillarOperationalReadiness;
          }
        default:
          {
            return false;
          }
      }
    };
    $[2] = answers;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const isStepComplete = t4;
  let t5;
  if ($[4] !== currentStep) {
    t5 = function handleNext() {
      if (currentStep < 11) {
        setCurrentStep(_temp);
      } else {
        setShowResult(true);
      }
      scrollToTop();
    };
    $[4] = currentStep;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleNext = t5;
  let t6;
  if ($[6] !== currentStep || $[7] !== showResult) {
    t6 = function handleBack() {
      if (showResult) {
        setShowResult(false);
      } else {
        if (currentStep > 0) {
          setCurrentStep(_temp2);
        }
      }
      scrollToTop();
    };
    $[6] = currentStep;
    $[7] = showResult;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleBack = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function handleReset() {
      setAnswers({});
      setCurrentStep(0);
      setShowResult(false);
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleReset = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = function handleRadio(key, value) {
      setAnswers(prev_1 => ({
        ...prev_1,
        [key]: value
      }));
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleRadio = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = function handleCheckbox(key_0, value_0) {
      setAnswers(prev_2 => {
        const current = prev_2[key_0] as string[] | undefined ?? [];
        const next = current.includes(value_0) ? current.filter(v => v !== value_0) : [...current, value_0];
        return {
          ...prev_2,
          [key_0]: next
        };
      });
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleCheckbox = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function handleText(key_1, value_1) {
      setAnswers(prev_3 => ({
        ...prev_3,
        [key_1]: value_1
      }));
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const handleText = t10;
  if (showResult) {
    let t11;
    if ($[13] !== answers || $[14] !== handleBack) {
      t11 = <ResultView answers={answers} onReset={handleReset} onBack={handleBack} />;
      $[13] = answers;
      $[14] = handleBack;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    return t11;
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = ["Glowpath x Retail Readiness", "About This Report", "The AU Retail Landscape", "What Buyers Actually Evaluate", "Why Brands Get Declined", "What Proof Looks Like", "Three Things You Need", "The Pitch Deck Problem", "The 5-Part Pitch Architecture", "What Buyers Are Excited About", "Where To Place Bets", "The Diagnostic"];
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const stepTitles = t11;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [<Lightbulb className="w-4 h-4" key="s0" />, <Quote className="w-4 h-4" key="s1" />, <Target className="w-4 h-4" key="s2" />, <BarChart3 className="w-4 h-4" key="s3" />, <AlertCircle className="w-4 h-4" key="s4" />, <ShieldCheck className="w-4 h-4" key="s5" />, <CheckCircle2 className="w-4 h-4" key="s6" />, <BookOpen className="w-4 h-4" key="s7" />, <Layers className="w-4 h-4" key="s8" />, <Lightbulb className="w-4 h-4" key="s9" />, <Target className="w-4 h-4" key="s10" />, <Award className="w-4 h-4" key="s11" />];
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const stepIcons = t12;
  const currentStepComplete = isStepComplete(currentStep);
  const t13 = currentStep + 1;
  const t14 = stepTitles[currentStep];
  let t15;
  if ($[18] !== t13 || $[19] !== t14) {
    t15 = <span className="text-sm font-medium text-gray-700">Step {t13} of {12}: {t14}</span>;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== currentStep || $[22] !== currentStepComplete) {
    t16 = Math.round((currentStep + (currentStepComplete ? 1 : 0)) / 12 * 100);
    $[21] = currentStep;
    $[22] = currentStepComplete;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t16) {
    t17 = <span className="text-sm text-gray-500">{t16}% complete</span>;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t15 || $[27] !== t17) {
    t18 = <div className="flex items-center justify-between mb-2">{t15}{t17}</div>;
    $[26] = t15;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const t19 = `${(currentStep + (currentStepComplete ? 1 : 0)) / 12 * 100}%`;
  let t20;
  if ($[29] !== t19) {
    t20 = <div className="h-1.5 bg-purple-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{
        width: t19,
        background: "linear-gradient(to right, #e91e8c, #c5f82a)"
      }} /></div>;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t18 || $[32] !== t20) {
    t21 = <div className="mb-6">{t18}{t20}</div>;
    $[31] = t18;
    $[32] = t20;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const t22 = showStepSelector ? "flex" : "hidden md:flex";
  let t23;
  if ($[34] !== t22) {
    t23 = cn("flex-wrap gap-2 mb-6", t22);
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== currentStep || $[37] !== isStepComplete) {
    t24 = stepTitles.map((title, i) => {
      const complete = isStepComplete(i);
      return <button key={i} onClick={() => setCurrentStep(i)} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer", i === currentStep ? "text-white shadow-sm" : complete ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100")} style={i === currentStep ? {
        background: "linear-gradient(135deg, #2d0b5a, #e91e8c)"
      } : undefined}>{complete && i !== currentStep && <CheckCircle2 className="w-3.5 h-3.5" />}{stepIcons[i]}<span className="hidden sm:inline">{title}</span></button>;
    });
    $[36] = currentStep;
    $[37] = isStepComplete;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t23 || $[40] !== t24) {
    t25 = <div className={t23}>{t24}</div>;
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  const t26 = currentStep === 0;
  const t27 = currentStep === 0 ? "text-gray-300 cursor-not-allowed" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100";
  let t28;
  if ($[42] !== t27) {
    t28 = cn("flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer", t27);
    $[42] = t27;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  let t30;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ChevronLeft className="w-3.5 h-3.5" />;
    t30 = <span>Prev</span>;
    $[44] = t29;
    $[45] = t30;
  } else {
    t29 = $[44];
    t30 = $[45];
  }
  let t31;
  if ($[46] !== handleBack || $[47] !== t26 || $[48] !== t28) {
    t31 = <button onClick={handleBack} disabled={t26} className={t28}>{t29}{t30}</button>;
    $[46] = handleBack;
    $[47] = t26;
    $[48] = t28;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  const t32 = !currentStepComplete;
  const t33 = currentStepComplete ? "text-gray-500 hover:text-gray-900 hover:bg-gray-100" : "text-gray-300 cursor-not-allowed";
  let t34;
  if ($[50] !== t33) {
    t34 = cn("flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer", t33);
    $[50] = t33;
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  let t35;
  let t36;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <span>Next</span>;
    t36 = <ChevronRight className="w-3.5 h-3.5" />;
    $[52] = t35;
    $[53] = t36;
  } else {
    t35 = $[52];
    t36 = $[53];
  }
  let t37;
  if ($[54] !== handleNext || $[55] !== t32 || $[56] !== t34) {
    t37 = <button onClick={handleNext} disabled={t32} className={t34}>{t35}{t36}</button>;
    $[54] = handleNext;
    $[55] = t32;
    $[56] = t34;
    $[57] = t37;
  } else {
    t37 = $[57];
  }
  let t38;
  if ($[58] !== t31 || $[59] !== t37) {
    t38 = <div className="flex items-center justify-between mb-4">{t31}{t37}</div>;
    $[58] = t31;
    $[59] = t37;
    $[60] = t38;
  } else {
    t38 = $[60];
  }
  let t39;
  if ($[61] !== currentStep) {
    t39 = currentStep === 0 && <CoverStep />;
    $[61] = currentStep;
    $[62] = t39;
  } else {
    t39 = $[62];
  }
  let t40;
  if ($[63] !== currentStep) {
    t40 = currentStep === 1 && <IntroStep />;
    $[63] = currentStep;
    $[64] = t40;
  } else {
    t40 = $[64];
  }
  let t41;
  if ($[65] !== answers || $[66] !== currentStep) {
    t41 = currentStep === 2 && <LandscapeStep answers={answers} onRadio={handleRadio} />;
    $[65] = answers;
    $[66] = currentStep;
    $[67] = t41;
  } else {
    t41 = $[67];
  }
  let t42;
  if ($[68] !== answers || $[69] !== currentStep) {
    t42 = currentStep === 3 && <BuyerEvaluationStep answers={answers} onRadio={handleRadio} />;
    $[68] = answers;
    $[69] = currentStep;
    $[70] = t42;
  } else {
    t42 = $[70];
  }
  let t43;
  if ($[71] !== answers || $[72] !== currentStep) {
    t43 = currentStep === 4 && <DeclineReasonsStep answers={answers} onCheckbox={handleCheckbox} />;
    $[71] = answers;
    $[72] = currentStep;
    $[73] = t43;
  } else {
    t43 = $[73];
  }
  let t44;
  if ($[74] !== answers || $[75] !== currentStep) {
    t44 = currentStep === 5 && <ProofMetricsStep answers={answers} onCheckbox={handleCheckbox} onText={handleText} />;
    $[74] = answers;
    $[75] = currentStep;
    $[76] = t44;
  } else {
    t44 = $[76];
  }
  let t45;
  if ($[77] !== answers || $[78] !== currentStep) {
    t45 = currentStep === 6 && <ThreeThingsStep answers={answers} onCheckbox={handleCheckbox} />;
    $[77] = answers;
    $[78] = currentStep;
    $[79] = t45;
  } else {
    t45 = $[79];
  }
  let t46;
  if ($[80] !== answers || $[81] !== currentStep) {
    t46 = currentStep === 7 && <PitchDeckStep answers={answers} onRadio={handleRadio} />;
    $[80] = answers;
    $[81] = currentStep;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  let t47;
  if ($[83] !== answers || $[84] !== currentStep) {
    t47 = currentStep === 8 && <PitchArchitectureStep answers={answers} onCheckbox={handleCheckbox} />;
    $[83] = answers;
    $[84] = currentStep;
    $[85] = t47;
  } else {
    t47 = $[85];
  }
  let t48;
  if ($[86] !== answers || $[87] !== currentStep) {
    t48 = currentStep === 9 && <BuyerExcitementStep answers={answers} onCheckbox={handleCheckbox} />;
    $[86] = answers;
    $[87] = currentStep;
    $[88] = t48;
  } else {
    t48 = $[88];
  }
  let t49;
  if ($[89] !== answers || $[90] !== currentStep) {
    t49 = currentStep === 10 && <MarketBetsStep answers={answers} onRadio={handleRadio} />;
    $[89] = answers;
    $[90] = currentStep;
    $[91] = t49;
  } else {
    t49 = $[91];
  }
  let t50;
  if ($[92] !== answers || $[93] !== currentStep) {
    t50 = currentStep === 11 && <DiagnosticStep answers={answers} onRadio={handleRadio} />;
    $[92] = answers;
    $[93] = currentStep;
    $[94] = t50;
  } else {
    t50 = $[94];
  }
  const t51 = currentStep === 0;
  const t52 = currentStep === 0 ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100";
  let t53;
  if ($[95] !== t52) {
    t53 = cn("flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer", t52);
    $[95] = t52;
    $[96] = t53;
  } else {
    t53 = $[96];
  }
  let t54;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <ChevronLeft className="w-4 h-4" />;
    $[97] = t54;
  } else {
    t54 = $[97];
  }
  let t55;
  if ($[98] !== handleBack || $[99] !== t51 || $[100] !== t53) {
    t55 = <button onClick={handleBack} disabled={t51} className={t53}>{t54}Back</button>;
    $[98] = handleBack;
    $[99] = t51;
    $[100] = t53;
    $[101] = t55;
  } else {
    t55 = $[101];
  }
  const t56 = !currentStepComplete;
  const t57 = currentStepComplete ? "text-white shadow-sm hover:opacity-90" : "bg-gray-100 text-gray-400 cursor-not-allowed";
  let t58;
  if ($[102] !== t57) {
    t58 = cn("flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer", t57);
    $[102] = t57;
    $[103] = t58;
  } else {
    t58 = $[103];
  }
  let t59;
  if ($[104] !== currentStepComplete) {
    t59 = currentStepComplete ? {
      background: "linear-gradient(135deg, #2d0b5a, #e91e8c)"
    } : undefined;
    $[104] = currentStepComplete;
    $[105] = t59;
  } else {
    t59 = $[105];
  }
  const t60 = currentStep === 11 ? "See My Results" : "Continue";
  let t61;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <ChevronRight className="w-4 h-4" />;
    $[106] = t61;
  } else {
    t61 = $[106];
  }
  let t62;
  if ($[107] !== handleNext || $[108] !== t56 || $[109] !== t58 || $[110] !== t59 || $[111] !== t60) {
    t62 = <button onClick={handleNext} disabled={t56} className={t58} style={t59}>{t60}{t61}</button>;
    $[107] = handleNext;
    $[108] = t56;
    $[109] = t58;
    $[110] = t59;
    $[111] = t60;
    $[112] = t62;
  } else {
    t62 = $[112];
  }
  let t63;
  if ($[113] !== t55 || $[114] !== t62) {
    t63 = <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">{t55}{t62}</div>;
    $[113] = t55;
    $[114] = t62;
    $[115] = t63;
  } else {
    t63 = $[115];
  }
  let t64;
  if ($[116] !== t38 || $[117] !== t39 || $[118] !== t40 || $[119] !== t41 || $[120] !== t42 || $[121] !== t43 || $[122] !== t44 || $[123] !== t45 || $[124] !== t46 || $[125] !== t47 || $[126] !== t48 || $[127] !== t49 || $[128] !== t50 || $[129] !== t63) {
    t64 = <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">{t38}{t39}{t40}{t41}{t42}{t43}{t44}{t45}{t46}{t47}{t48}{t49}{t50}{t63}</div>;
    $[116] = t38;
    $[117] = t39;
    $[118] = t40;
    $[119] = t41;
    $[120] = t42;
    $[121] = t43;
    $[122] = t44;
    $[123] = t45;
    $[124] = t46;
    $[125] = t47;
    $[126] = t48;
    $[127] = t49;
    $[128] = t50;
    $[129] = t63;
    $[130] = t64;
  } else {
    t64 = $[130];
  }
  let t65;
  if ($[131] !== t21 || $[132] !== t25 || $[133] !== t64) {
    t65 = <div className="max-w-5xl mx-auto" ref={wizardRef}>{t21}{t25}{t64}</div>;
    $[131] = t21;
    $[132] = t25;
    $[133] = t64;
    $[134] = t65;
  } else {
    t65 = $[134];
  }
  return t65;
}

// --- Cover Step: Glowpath x Retail Readiness ---
function _temp2(prev_0) {
  return prev_0 - 1;
}
function _temp(prev) {
  return prev + 1;
}
function CoverStep() {
  const $ = _c(13);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      background: "#e91e8c"
    };
    t1 = <div className="absolute top-4 left-4 w-24 h-24 border border-white/20 rounded-full" />;
    t2 = <div className="absolute bottom-4 right-4 w-20 h-20 border border-white/20 rounded-full" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="text-4xl sm:text-6xl font-black tracking-tight" style={{
      color: "#c5f82a"
    }}>GLOWPATH</h2>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-lg font-medium" style={{
      color: "#c5f82a"
    }}>x</p>;
    t5 = <h3 className="text-2xl sm:text-4xl font-black text-white leading-tight">Retail Readiness Snapshot</h3>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="text-xs sm:text-sm font-bold uppercase tracking-widest" style={{
      color: "#c5f82a"
    }}>Australian Edition · First Edition · 2026</p>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden" style={t0}>{t1}{t2}<div className="relative z-10 space-y-6">{t3}{t4}{t5}{t6}<div className="pt-4 space-y-1"><p className="text-sm text-white/80 uppercase tracking-wider">Alysha Richards · Co-Founder, Glowpath</p><p className="text-sm italic text-white/70">We help consumer brands win at retail from first pitch to global scale</p></div></div></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h4 className="text-sm font-semibold text-gray-900 mb-2">What to expect from this assessment</h4>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" /><span>Walk through real data from AU retail buyers with active ranging authority</span></li>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" /><span>Self-assess against the 5 pillars buyers use to evaluate brands</span></li>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" /><span>Get personalised recommendations based on your answers</span></li>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div>{t7}<div className="mt-6 rounded-xl p-5 bg-gray-50 border border-gray-200">{t8}<ul className="space-y-2 text-sm text-gray-600">{t9}{t10}{t11}<li className="flex gap-2"><ArrowRight className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" /><span>Takes approximately 5-8 minutes to complete</span></li></ul></div></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  return t12;
}

// --- Intro Step: About This Report ---

function IntroStep() {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">About This Report</h2></div><p className="text-gray-600 text-sm">Retail Readiness Snapshot 2026 - Australian Edition</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      background: "#2d0b5a"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h3 className="text-lg font-black mb-3" style={{
      color: "#c5f82a"
    }}>FROM THE DESK OF ALYSHA RICHARDS</h3>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      color: "#e8e0f7"
    };
    t4 = <p>{"15 years on the brand side of retail, leading launches at Priceline, Sephora, MECCA, Atomica, Style Runner, Woolworths and Coles. Delivered Priceline's biggest skincare launch and scaled frank body's retail business by 3x."}</p>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="rounded-2xl p-6 mb-6" style={t1}>{t2}<div className="space-y-3 text-sm leading-relaxed" style={t3}>{t4}<p className="font-bold" style={{
          color: "#c5f82a"
        }}>{"\"I know how they think, what they need, and exactly what costs brands the shelf. I built this survey to put that intelligence directly in founders' hands.\""}</p></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      background: "#1a0a4e"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      color: "#e8e0f7"
    };
    t8 = <p>This snapshot captures the unfiltered perspective of the people sitting across the table from you - AU retail buyers and category managers across mass beauty, pharmacy, specialty and department store.</p>;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="rounded-2xl p-6 mb-6" style={t6}><div className="space-y-3 text-sm leading-relaxed" style={t7}>{t8}<p className="font-bold" style={{
          color: "#c5f82a"
        }}>Every respondent has active ranging authority. These are the people who decide.</p><p>{"Use this to pressure-test your pitch. Check your assumptions. And if you recognise your brand in these pages, that's the point."}</p></div></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      background: "#0f3d3d"
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h4 className="text-sm font-bold mb-3" style={{
      color: "#c5f82a"
    }}>Who Responded</h4>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div>{t0}{t5}{t9}<div className="rounded-xl p-5" style={t10}>{t11}<div className="space-y-2">{[{
            label: "Mass Beauty",
            detail: "Woolworths Beauty, Coles, Big W"
          }, {
            label: "Pharmacy / Health",
            detail: "Chemist Warehouse, Priceline"
          }, {
            label: "Specialty Beauty",
            detail: "Sephora, MECCA, Atomica"
          }, {
            label: "Department Store",
            detail: "Myer, David Jones"
          }].map(_temp3)}</div></div></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  return t12;
}

// --- Step 1: The AU Retail Landscape ---
function _temp3(r) {
  return <div key={r.label} className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{
      background: "#c5f82a"
    }} /><span className="text-sm font-medium text-white">{r.label}</span><span className="text-xs" style={{
      color: "#8bc5a0"
    }}>- {r.detail}</span></div>;
}
function LandscapeStep(t0) {
  const $ = _c(17);
  const {
    answers,
    onRadio
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">The AU Retail Landscape</h2></div><p className="text-gray-600 text-sm">Three realities shaping how brands get on shelf in Australia in 2026.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#1a0a4e"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{
      background: "#e91e8c",
      color: "white"
    }}>1</span>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-sm font-bold mb-1" style={{
      color: "#c5f82a"
    }}>"The shelf is full. The white space is narrow."</p>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="rounded-xl p-4" style={t2}><div className="flex gap-3">{t3}<div>{t4}<p className="text-sm leading-relaxed" style={{
            color: "#e8e0f7"
          }}>Mass and pharmacy shelves have never been more contested. Every major retailer is rationalising range, not expanding it. Getting on shelf is not harder - staying on shelf is.</p></div></div></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      background: "#1a0a4e"
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{
      background: "#e91e8c",
      color: "white"
    }}>2</span>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-sm font-bold mb-1" style={{
      color: "#c5f82a"
    }}>"Buyers are buying category strategies, not products."</p>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="rounded-xl p-4" style={t6}><div className="flex gap-3">{t7}<div>{t8}<p className="text-sm leading-relaxed" style={{
            color: "#e8e0f7"
          }}>Walking in with a great product is not enough. Buyers want to know: what is the incremental customer? Where is the white space?</p></div></div></div>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      background: "#1a0a4e"
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{
      background: "#e91e8c",
      color: "white"
    }}>3</span>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="text-sm font-bold mb-1" style={{
      color: "#c5f82a"
    }}>"The cost of going in unprepared has never been higher."</p>;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="space-y-4 mb-8">{t5}{t9}<div className="rounded-xl p-4" style={t10}><div className="flex gap-3">{t11}<div>{t12}<p className="text-sm leading-relaxed" style={{
              color: "#e8e0f7"
            }}>A declined pitch is not just a no. It is a closed door for 12-24 months. Buyers talk. Category managers move between retailers.</p></div></div></div></div>;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [{
      value: "crowded-shelf",
      label: "I'm worried about standing out in a crowded shelf"
    }, {
      value: "category-thinking",
      label: "I need to shift from product-first to category-first thinking"
    }, {
      value: "first-time",
      label: "I haven't pitched yet but want to get it right first time"
    }, {
      value: "declined",
      label: "I've been declined and need to understand why"
    }, {
      value: "not-sure",
      label: "Not sure yet - I'm still exploring"
    }];
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  if ($[14] !== answers || $[15] !== onRadio) {
    t15 = <div>{t1}{t13}<PromptSection title="Which of these concerns resonates most with where you are right now?"><div className="space-y-2">{t14.map(option => <label key={option.value} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", answers.landscapeConcern === option.value ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="radio" name="landscapeConcern" value={option.value} checked={answers.landscapeConcern === option.value} onChange={() => onRadio("landscapeConcern", option.value)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-indigo-600 border-gray-300 focus:ring-indigo-500" /><span className="text-sm text-gray-700 leading-relaxed">{option.label}</span></label>)}</div></PromptSection></div>;
    $[14] = answers;
    $[15] = onRadio;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  return t15;
}

// --- Step 2: What Buyers Actually Evaluate ---

function BuyerEvaluationStep(t0) {
  const $ = _c(16);
  const {
    answers,
    onRadio
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      label: "Category Growth Opportunity",
      pct: 100
    }, {
      label: "Proven Sales Performance",
      pct: 90
    }, {
      label: "Product Differentiation",
      pct: 90
    }, {
      label: "Margin Structure",
      pct: 80
    }, {
      label: "Innovation / Newness",
      pct: 65
    }, {
      label: "Brand Awareness / Community",
      pct: 65
    }, {
      label: "Packaging & Shelf Appeal",
      pct: 45
    }, {
      label: "Founder Credibility",
      pct: 10
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const factors = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">What Buyers Actually Evaluate</h2></div><p className="text-gray-600 text-sm">Ranked factors AU retail buyers use when evaluating a new brand.</p></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="rounded-xl p-5 mb-4 space-y-3" style={{
      background: "#1a0a4e"
    }}>{factors.map(_temp4)}</div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      background: "#e91e8c"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="rounded-xl p-4 mb-8" style={t4}><div className="flex gap-3"><Lightbulb className="w-5 h-5 text-white shrink-0 mt-0.5" /><p className="text-sm text-white font-semibold leading-relaxed">The top three factors are all category-first. If your pitch is product-first, you are answering the wrong question.</p></div></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [{
      value: "founder-story",
      label: "Our founder story and why we started"
    }, {
      value: "product-quality",
      label: "Product quality and ingredients"
    }, {
      value: "category-growth",
      label: "Category growth opportunity and white space"
    }, {
      value: "sales-data",
      label: "Sales data and commercial proof"
    }, {
      value: "not-sure",
      label: "Not sure / I don't have a pitch yet"
    }];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== answers || $[7] !== onRadio) {
    t7 = <PromptSection title="What does your current pitch lead with?"><div className="space-y-2">{t6.map(option => <label key={option.value} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", answers.pitchLeadsWith === option.value ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="radio" name="pitchLeadsWith" value={option.value} checked={answers.pitchLeadsWith === option.value} onChange={() => onRadio("pitchLeadsWith", option.value)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-indigo-600 border-gray-300 focus:ring-indigo-500" /><span className="text-sm text-gray-700 leading-relaxed">{option.label}</span></label>)}</div></PromptSection>;
    $[6] = answers;
    $[7] = onRadio;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["1", "2", "3", "4", "5"];
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== answers || $[11] !== onRadio) {
    t9 = <PromptSection title="Rate your category growth argument (1-5)" description="1 = No category argument, 5 = Full competitive analysis with white space mapping"><div className="grid grid-cols-3 sm:grid-cols-5 gap-2">{t8.map(val => {
          const labels = {
            "1": "No category argument",
            "2": "Vague awareness",
            "3": "Some category data",
            "4": "Strong category case",
            "5": "Full competitive analysis"
          };
          return <label key={val} className={cn("flex flex-col items-center gap-1 px-3 py-3 rounded-xl border cursor-pointer transition-all text-center", answers.categoryArgumentRating === val ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="radio" name="categoryArgumentRating" value={val} checked={answers.categoryArgumentRating === val} onChange={() => onRadio("categoryArgumentRating", val)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-indigo-600 border-gray-300 focus:ring-indigo-500" /><span className="text-lg font-bold text-gray-900">{val}</span><span className="text-[10px] text-gray-500 text-center leading-tight break-words">{labels[val]}</span></label>;
        })}</div></PromptSection>;
    $[10] = answers;
    $[11] = onRadio;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t7 || $[14] !== t9) {
    t10 = <div>{t2}{t3}{t5}{t7}{t9}</div>;
    $[13] = t7;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}

// --- Step 3: Why Brands Get Declined ---
function _temp4(f) {
  return <div key={f.label}><div className="flex items-center justify-between mb-1"><span className="text-xs font-medium" style={{
        color: "#c5f82a"
      }}>{f.label}</span><span className="text-xs font-bold text-white">{f.pct}%</span></div><div className="h-2.5 rounded-full overflow-hidden" style={{
      background: "#2d0b5a"
    }}><div className="h-full rounded-full transition-all duration-700" style={{
        width: `${f.pct}%`,
        background: f.pct >= 80 ? "#c5f82a" : f.pct >= 60 ? "#8bd42a" : "#6b7280"
      }} /></div></div>;
}
function DeclineReasonsStep(t0) {
  const $ = _c(18);
  const {
    answers,
    onCheckbox
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      label: "Lack of Demand/Awareness",
      pct: 80
    }, {
      label: "Category Too Saturated",
      pct: 60
    }, {
      label: "Unclear Brand Positioning",
      pct: 50
    }, {
      label: "Weak Retail Pitch",
      pct: 50
    }, {
      label: "Margin Challenges",
      pct: 40
    }, {
      label: "Operational Readiness Issues",
      pct: 40
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const declineReasons = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">Why Brands Get Declined</h2></div><p className="text-gray-600 text-sm">The top reasons AU retail buyers pass on emerging brands.</p></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      background: "#1a0a4e"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="rounded-2xl p-6 mb-4" style={t3}><div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{declineReasons.map(_temp5)}</div></div>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      background: "#e91e8c"
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="rounded-xl p-4 mb-6" style={t5}><div className="flex gap-3"><Lightbulb className="w-5 h-5 text-white shrink-0 mt-0.5" /><p className="text-sm text-white font-semibold leading-relaxed">Retailers are not your marketing strategy. If people do not know your brand exists before you get on shelf, the shelf will not save you.</p></div></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      background: "#e91e8c"
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{
      color: "#c5f82a"
    }}>GLOWPATH VERDICT</p>;
    t9 = <h3 className="text-xl sm:text-2xl font-black text-white leading-tight mb-1">{"These aren't six separate problems."}</h3>;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h3 className="text-xl sm:text-2xl font-black leading-tight mb-4" style={{
      color: "#c5f82a"
    }}>{"They're one."}</h3>;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="h-0.5 mb-6" style={{
      background: "#c5f82a"
    }} />;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="rounded-xl p-5" style={{
      background: "#2d0b5a"
    }}><h4 className="text-sm font-bold text-white mb-2">{"Lack of demand & weak positioning are the same failure."}</h4><p className="text-sm text-white/80 leading-relaxed">{"If buyers don't know your brand exists, and can't articulate what you stand for, that's one problem: you haven't built commercial proof outside of retail before you tried to get in."}</p></div>;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="rounded-xl p-5" style={{
      background: "#2d0b5a"
    }}><h4 className="text-sm font-bold text-white mb-2">{"A declined pitch leaves a mark."}</h4><p className="text-sm text-white/80 leading-relaxed">{"Buyers remember who came in unprepared. A weak first impression doesn't just cost you this meeting - it costs you the next 12-24 months. In AU retail, reputation travels faster than you think."}</p></div>;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      background: "#2d0b5a"
    };
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="rounded-2xl p-6 mb-8" style={t7}>{t8}{t9}{t10}{t11}<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{t12}{t13}<div className="rounded-xl p-5" style={t14}><h4 className="text-sm font-bold italic text-white mb-2" style={{
            color: "#c5f82a"
          }}>{"Operational issues signal commercial immaturity"}</h4><p className="text-sm text-white/80 leading-relaxed">{"40% of buyers cited operational readiness as a reason they passed. Not product quality. Not brand story. The basics - stock availability, lead times, fulfilment capacity. These are solvable. Most brands just don't solve them before they pitch."}</p></div></div></div>;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== answers || $[16] !== onCheckbox) {
    t16 = <div>{t2}{t4}{t6}{t15}<PromptSection title="Which of these risks apply to your brand right now?" description="Select all that apply."><div className="space-y-2">{[{
            value: "limited-awareness",
            label: "Limited brand awareness outside our existing audience"
          }, {
            value: "saturated-category",
            label: "Operating in a saturated category"
          }, {
            value: "unclear-positioning",
            label: "Brand positioning could be clearer"
          }, {
            value: "weak-pitch",
            label: "Pitch deck needs work"
          }, {
            value: "tight-margins",
            label: "Margin structure is tight"
          }, {
            value: "operational-concerns",
            label: "Operational capacity concerns"
          }].map(option => {
            const isChecked = answers.declineRisks?.includes(option.value) ?? false;
            return <label key={option.value} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", isChecked ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="checkbox" checked={isChecked} onChange={() => onCheckbox("declineRisks", option.value)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" /><span className="text-sm text-gray-700 leading-relaxed">{option.label}</span></label>;
          })}</div></PromptSection></div>;
    $[15] = answers;
    $[16] = onCheckbox;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  return t16;
}

// --- Step 4: What Proof Looks Like ---
function _temp5(r) {
  return <div key={r.label} className="flex items-center gap-3"><div className="flex-shrink-0 w-16 h-16 rounded-lg flex items-center justify-center text-xl font-black" style={{
      background: "#c5f82a",
      color: "#1a0a4e"
    }}>{r.pct}%</div><span className="text-xs font-bold uppercase tracking-wide" style={{
      color: "#c5f82a"
    }}>{r.label}</span></div>;
}
function ProofMetricsStep(t0) {
  const $ = _c(16);
  const {
    answers,
    onCheckbox,
    onText
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      label: "Strong DTC Sales",
      pct: 70,
      note: "Your own channel, your own proof"
    }, {
      label: "Proven Retail Performance",
      pct: 70,
      note: "Already won a shelf, somewhere"
    }, {
      label: "Community Engagement",
      pct: 60,
      note: "Active, vocal, loyal"
    }, {
      label: "Social Traction",
      pct: 60,
      note: "Community that converts"
    }, {
      label: "Repeat Purchase Rate",
      pct: 50,
      note: "Proves the product works"
    }, {
      label: "Strong Press / PR",
      pct: 20,
      note: "Third-party validation"
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const proofMetrics = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">What Proof Looks Like</h2></div><p className="text-gray-600 text-sm">The proof metrics AU buyers look for when assessing brand credibility.</p></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="rounded-xl p-5 mb-4 space-y-3" style={{
      background: "#0f3d3d"
    }}>{proofMetrics.map(_temp6)}</div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      background: "#e91e8c"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="rounded-xl p-4 mb-8" style={t4}><div className="flex gap-3"><Lightbulb className="w-5 h-5 text-white shrink-0 mt-0.5" /><p className="text-sm text-white font-semibold leading-relaxed">DTC sales and proven retail performance were cited equally at 70%. Build one before you pitch for the other.</p></div></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== answers.proofPoints || $[6] !== onCheckbox) {
    t6 = <PromptSection title="Which proof points can you demonstrate today?" description="Select all that apply."><div className="space-y-2">{[{
          value: "dtc-sales",
          label: "Strong DTC sales data"
        }, {
          value: "retail-performance",
          label: "Retail performance elsewhere"
        }, {
          value: "community",
          label: "Engaged community (UGC, reviews)"
        }, {
          value: "social",
          label: "Social following that converts"
        }, {
          value: "repeat-purchase",
          label: "Repeat purchase data"
        }, {
          value: "press-pr",
          label: "Press/PR coverage"
        }].map(option => {
          const isChecked = answers.proofPoints?.includes(option.value) ?? false;
          return <label key={option.value} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", isChecked ? "border-emerald-300 bg-emerald-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="checkbox" checked={isChecked} onChange={() => onCheckbox("proofPoints", option.value)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" /><span className="text-sm text-gray-700 leading-relaxed">{option.label}</span></label>;
        })}</div></PromptSection>;
    $[5] = answers.proofPoints;
    $[6] = onCheckbox;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = answers.strongestProofPoint ?? "";
  let t8;
  if ($[8] !== onText) {
    t8 = e => onText("strongestProofPoint", e.target.value);
    $[8] = onText;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7 || $[11] !== t8) {
    t9 = <PromptSection title="What is your strongest proof point? (optional)"><input type="text" value={t7} onChange={t8} placeholder="e.g. 2,000 monthly DTC orders with 40% repeat rate" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" /></PromptSection>;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t6 || $[14] !== t9) {
    t10 = <div>{t2}{t3}{t5}{t6}{t9}</div>;
    $[13] = t6;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}

// --- Step 5: Three Things You Need ---
function _temp6(m) {
  return <div key={m.label}><div className="flex items-center justify-between mb-1"><div><span className="text-sm font-medium" style={{
          color: "#c5f82a"
        }}>{m.label}</span><span className="text-xs ml-2" style={{
          color: "#8bc5a0"
        }}>- {m.note}</span></div><span className="text-sm font-bold text-white">{m.pct}%</span></div><div className="h-3 rounded-full overflow-hidden" style={{
      background: "#1a5c5c"
    }}><div className="h-full rounded-full" style={{
        width: `${m.pct}%`,
        background: "#c5f82a"
      }} /></div></div>;
}
function ThreeThingsStep(t0) {
  const $ = _c(10);
  const {
    answers,
    onCheckbox
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      num: "01",
      title: "VELOCITY PROOF",
      headline: "Show me it's already selling.",
      body: "DTC data. Retail sell-through from another channel. Repeat purchase rate. Any evidence that when consumers find your product, they buy it again. Without this, you're asking a buyer to take a risk with no evidence."
    }, {
      num: "02",
      title: "COMMUNITY SIGNAL",
      headline: "Show me people care.",
      body: "Not just followers - engagement. UGC. Saves. Reviews. The AU market is small. Word of mouth travels fast. Buyers want to see a community that will pull the product off the shelf the day it lands."
    }, {
      num: "03",
      title: "CATEGORY ARGUMENT",
      headline: "Show me why the shelf is incomplete without you.",
      body: "Competitive set analysis. White space mapping. The incremental customer. Lay out exactly what the category looks like today, and what changes when your brand is ranged. This is the argument that wins."
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const threeThings = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">{"Three Things You Need"}</h2></div><p className="text-gray-600 text-sm">{"Before you walk into a buyer meeting."}</p></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      background: "#1a0a4e"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{
      color: "#c5f82a"
    }}>GLOWPATH VERDICT</p>;
    t5 = <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight mb-1">{"Before you walk into a buyer meeting, you need"}</h3>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="rounded-2xl p-6 mb-6" style={t3}>{t4}{t5}<h3 className="text-2xl sm:text-3xl font-black leading-tight" style={{
        color: "#c5f82a"
      }}>{"three things."}</h3></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">{threeThings.map(_temp7)}</div>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== answers || $[8] !== onCheckbox) {
    t8 = <div>{t2}{t6}{t7}<PromptSection title="Which of these three things can you confidently demonstrate today?" description="Select all that apply."><div className="space-y-2">{[{
            value: "velocity-proof",
            label: "Velocity Proof - I have sales data that shows my product sells"
          }, {
            value: "community-signal",
            label: "Community Signal - I have engaged community that will drive off-shelf pull"
          }, {
            value: "category-argument",
            label: "Category Argument - I can articulate the category growth case with data"
          }].map(option => {
            const isChecked = answers.threeThingsConfidence?.includes(option.value) ?? false;
            return <label key={option.value} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", isChecked ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="checkbox" checked={isChecked} onChange={() => onCheckbox("threeThingsConfidence", option.value)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" /><span className="text-sm text-gray-700 leading-relaxed">{option.label}</span></label>;
          })}</div></PromptSection></div>;
    $[7] = answers;
    $[8] = onCheckbox;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}

// --- Step 6: The Pitch Deck Problem ---
function _temp7(thing) {
  return <div key={thing.num} className="rounded-xl p-5" style={{
    background: "#9b1763"
  }}><div className="flex items-center gap-2 mb-3"><span className="text-sm font-black" style={{
        color: "#c5f82a"
      }}>{thing.num}</span><span className="text-[10px] font-bold uppercase tracking-wider text-white/70">{thing.title}</span></div><p className="text-sm font-bold text-white mb-2">{thing.headline}</p><p className="text-sm text-white/80 leading-relaxed">{thing.body}</p></div>;
}
function PitchDeckStep(t0) {
  const $ = _c(16);
  const {
    answers,
    onRadio
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">The Pitch Deck Problem</h2></div><p className="text-gray-600 text-sm">{"Survey data + buyer voice."}</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "#0f4e4e"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{
      color: "#c5f82a"
    }}>{"SURVEY DATA + BUYER VOICE"}</p>;
    t4 = <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight mb-4">{"THE PITCH DECK PROBLEM"}</h3>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="text-sm italic mb-4" style={{
      color: "#c5f82a"
    }}>{"What buyers wish founders did better:"}</p>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div>{t3}{t4}{t5}<ul className="space-y-3">{["Lead with commercial story - not your origin story", "Prove category growth, not just brand potential", "Include retailer-specific pricing, margin & promo depth", "Show the white space you are filling - with data", "Keep it tight. Buyers are time poor. Always."].map(_temp8)}</ul></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{
      color: "#c5f82a"
    }}>{"DIRECT FROM AU BUYERS"}</p>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="rounded-lg p-4" style={{
      background: "#e8f5e0"
    }}><p className="text-sm text-gray-800 italic leading-relaxed mb-2">{"\"Don't try and sell me on the fact that the product looks great - sell me on how well it will grow the category.\""}</p><p className="text-xs font-bold text-gray-900">{"- Mass Beauty Buyer"}</p></div>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="rounded-lg p-4" style={{
      background: "#e8f5e0"
    }}><p className="text-sm text-gray-800 italic leading-relaxed mb-2">{"\"Buyers are so time poor. Think smarter, not harder. A 50-page deck won't get read.\""}</p><p className="text-xs font-bold text-gray-900">{"- Mass Beauty Buyer"}</p></div>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="rounded-lg p-4" style={{
      background: "#e8f5e0"
    }}><p className="text-sm text-gray-800 italic leading-relaxed mb-2">{"\"Retailer-specific commercial story, pricing and relevant future promotional plans - that's what I need to see.\""}</p><p className="text-xs font-bold text-gray-900">{"- Pharmacy Buyer"}</p></div>;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">{t6}<div>{t7}<div className="space-y-4">{t8}{t9}{t10}<div className="rounded-lg p-4" style={{
            background: "#e8f5e0"
          }}><p className="text-sm text-gray-800 italic leading-relaxed mb-2">{"\"Not researching current range and what gap they have to fill. That's the most common mistake I see.\""}</p><p className="text-xs font-bold text-gray-900">{"- Department Store Buyer"}</p></div></div></div></div>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="rounded-2xl p-6 mb-6" style={t2}>{t11}<div className="mt-6 rounded-xl p-5" style={{
        background: "#e91e8c"
      }}><p className="text-sm font-bold italic text-white leading-relaxed mb-2">{"\"As a buyer evaluating both established and emerging brands, I look for founders who have a strong understanding of the competitive landscape. Equally important is recognising that you're not just selling a product - you're offering a solution and delivering a complete customer experience.\""}</p><p className="text-xs font-bold text-white/90">{"Albina Reale Head of Beauty | Buying Department"}</p><p className="text-xs text-white/70">{"Chemist Warehouse | Ultra Beauty"}</p></div></div>;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [{
      value: "none",
      label: "I don't have one yet"
    }, {
      value: "generic",
      label: "Generic brand deck I send to everyone"
    }, {
      value: "brand-story",
      label: "Under 20 slides but leads with brand story"
    }, {
      value: "retailer-specific",
      label: "Retailer-specific, commercial-first, under 20 slides"
    }];
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== answers || $[14] !== onRadio) {
    t14 = <div>{t1}{t12}<PromptSection title="How would you describe your current pitch deck?"><div className="space-y-2">{t13.map(option => <label key={option.value} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", answers.pitchDeckStatus === option.value ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="radio" name="pitchDeckStatus" value={option.value} checked={answers.pitchDeckStatus === option.value} onChange={() => onRadio("pitchDeckStatus", option.value)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-indigo-600 border-gray-300 focus:ring-indigo-500" /><span className="text-sm text-gray-700 leading-relaxed">{option.label}</span></label>)}</div></PromptSection></div>;
    $[13] = answers;
    $[14] = onRadio;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  return t14;
}

// --- Step 7: The 5-Part Pitch Architecture ---
function _temp8(item, i) {
  return <li key={i} className="flex gap-3 text-sm text-white"><div className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-xs font-bold" style={{
      background: "#c5f82a",
      color: "#0f4e4e"
    }}>✓</div><span className="leading-relaxed">{item}</span></li>;
}
function PitchArchitectureStep(t0) {
  const $ = _c(26);
  const {
    answers,
    onCheckbox
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== answers || $[1] !== onCheckbox) {
    const parts = [{
      num: 1,
      title: "The Category Case",
      label: "The Category Case",
      desc: "Here is what the category looks like today. Here is the gap. Here is the customer you are not currently reaching."
    }, {
      num: 2,
      title: "The Proof Stack",
      label: "The Proof Stack",
      desc: "DTC data. Social proof. Retail track record. Repeat purchase. The evidence your brand has earned the right to a shelf."
    }, {
      num: 3,
      title: "The Retailer Fit",
      label: "The Retailer Fit",
      desc: "Why this retailer. Why now. How your brand fits their shopper, their price architecture, their category strategy."
    }, {
      num: 4,
      title: "The Commercial Terms",
      label: "The Commercial Terms",
      desc: "Sell price. Indicative cost. Margin. Promo depth. Make it easy to say yes - have the numbers ready before they ask."
    }, {
      num: 5,
      title: "The Launch Plan",
      label: "The Launch Plan",
      desc: "How you will activate. PR. Sampling. Events. Digital. What you are investing to make the launch successful for them."
    }];
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">The 5-Part Pitch Architecture</h2></div><p className="text-gray-600 text-sm">The structure that turns buyer meetings into ranging decisions.</p></div>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t6 = <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-8">{parts.map(_temp9)}</div>;
    const t8 = <div className="space-y-2">{parts.map(p_0 => {
        const isChecked = answers.confidentParts?.includes(p_0.title) ?? false;
        return <label key={p_0.title} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", isChecked ? "border-emerald-300 bg-emerald-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="checkbox" checked={isChecked} onChange={() => onCheckbox("confidentParts", p_0.title)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" /><span className="text-sm text-gray-700 leading-relaxed">{p_0.label}</span></label>;
      })}</div>;
    if ($[11] !== t8) {
      t7 = <PromptSection title="Which of these 5 parts do you feel most confident about?" description="Select all that apply.">{t8}</PromptSection>;
      $[11] = t8;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    T0 = PromptSection;
    t3 = "Which needs the most work?";
    t4 = "Select all that apply.";
    t1 = "space-y-2";
    t2 = parts.map(p_1 => {
      const isChecked_0 = answers.needsWorkParts?.includes(p_1.title) ?? false;
      return <label key={p_1.title} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", isChecked_0 ? "border-amber-300 bg-amber-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="checkbox" checked={isChecked_0} onChange={() => onCheckbox("needsWorkParts", p_1.title)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-amber-600 border-gray-300 rounded focus:ring-amber-500" /><span className="text-sm text-gray-700 leading-relaxed">{p_1.label}</span></label>;
    });
    $[0] = answers;
    $[1] = onCheckbox;
    $[2] = T0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    T0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[13] !== t1 || $[14] !== t2) {
    t8 = <div className={t1}>{t2}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== T0 || $[17] !== t3 || $[18] !== t4 || $[19] !== t8) {
    t9 = <T0 title={t3} description={t4}>{t8}</T0>;
    $[16] = T0;
    $[17] = t3;
    $[18] = t4;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t9) {
    t10 = <div>{t5}{t6}{t7}{t9}</div>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}

// --- Step 8: What AU Buyers Are Excited About ---
function _temp9(p) {
  return <div key={p.num} className="rounded-xl p-4 text-white" style={{
    background: p.num % 2 === 0 ? "#2d0b5a" : "#1a0a4e"
  }}><div className="text-sm font-bold mb-1" style={{
      color: "#c5f82a"
    }}>0{p.num}</div><p className="text-sm font-semibold text-white mb-1">{p.title}</p><p className="text-sm leading-relaxed" style={{
      color: "#c9b8e8"
    }}>{p.desc}</p></div>;
}
function BuyerExcitementStep(t0) {
  const $ = _c(45);
  const {
    answers,
    onCheckbox
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== answers.excitedCategories || $[1] !== onCheckbox) {
    const categories = [{
      title: "Korean Skincare",
      desc: "Sub $30 RRP. High efficacy, accessible price, the gap is real and the demand is there.",
      bg: "#1a0a4e"
    }, {
      title: "Active Clinical Skincare",
      desc: "The bridge between entry-level and cosmeceutical.",
      bg: "#2563eb"
    }, {
      title: "Functional Beauty x Wellness",
      desc: "Year-round hybrids crossing health and beauty.",
      bg: "#7c3aed"
    }, {
      title: "Body & Makeup Innovation",
      desc: "Genuinely new - not reformulations with new packaging.",
      bg: "#0f766e"
    }, {
      title: "Masstige / Luxe-for-Less",
      desc: "Prestige finish at a mass price point.",
      bg: "#1d4ed8"
    }, {
      title: "Founder-Led Brands",
      desc: "Authenticity and community that big brands can't manufacture.",
      bg: "#9333ea"
    }];
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">{"What AU Buyers Are Excited About"}</h2></div><p className="text-gray-600 text-sm">{"Categories and brand types actively on AU buyers' radars - right now."}</p></div>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t8;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        background: "#6b0f54"
      };
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <p className="text-sm font-bold italic mb-4" style={{
        color: "#c5f82a"
      }}>{"Categories and brand types actively on AU buyers' radars - right now"}</p>;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    const t10 = <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{categories.map(_temp10)}</div>;
    if ($[13] !== t10) {
      t6 = <div className="rounded-2xl p-6 mb-6" style={t8}>{t9}{t10}</div>;
      $[13] = t10;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="rounded-xl p-4 mb-8" style={{
        background: "#e91e8c"
      }}><p className="text-sm text-white font-bold leading-relaxed">{"Korean Skincare, Active Clinical and Body & Makeup are the three categories with the most open white space in AU retail right now. If your brand plays in any of these spaces and you're not pitching - someone else is."}</p></div>;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    T0 = PromptSection;
    t3 = "Which of these categories does your brand play in?";
    t4 = "Select all that apply.";
    t1 = "space-y-2";
    t2 = categories.map(cat_0 => {
      const isChecked = answers.excitedCategories?.includes(cat_0.title) ?? false;
      return <label key={cat_0.title} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", isChecked ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="checkbox" checked={isChecked} onChange={() => onCheckbox("excitedCategories", cat_0.title)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" /><span className="text-sm text-gray-700 leading-relaxed">{cat_0.title}</span></label>;
    });
    $[0] = answers.excitedCategories;
    $[1] = onCheckbox;
    $[2] = T0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    T0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[16] !== answers.excitedCategories) {
    t8 = answers.excitedCategories?.includes("Other") ?? false ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50";
    $[16] = answers.excitedCategories;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", t8);
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== answers.excitedCategories) {
    t10 = answers.excitedCategories?.includes("Other") ?? false;
    $[20] = answers.excitedCategories;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== onCheckbox) {
    t11 = () => onCheckbox("excitedCategories", "Other");
    $[22] = onCheckbox;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <input type="checkbox" checked={t10} onChange={t11} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-sm text-gray-700 leading-relaxed">{"None of the above / Other category"}</span>;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t9) {
    t14 = <label className={t9}>{t12}{t13}</label>;
    $[28] = t12;
    $[29] = t9;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t1 || $[32] !== t14 || $[33] !== t2) {
    t15 = <div className={t1}>{t2}{t14}</div>;
    $[31] = t1;
    $[32] = t14;
    $[33] = t2;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== T0 || $[36] !== t15 || $[37] !== t3 || $[38] !== t4) {
    t16 = <T0 title={t3} description={t4}>{t15}</T0>;
    $[35] = T0;
    $[36] = t15;
    $[37] = t3;
    $[38] = t4;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t16 || $[41] !== t5 || $[42] !== t6 || $[43] !== t7) {
    t17 = <div>{t5}{t6}{t7}{t16}</div>;
    $[40] = t16;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
}

// --- Step 9: Where To Place Bets ---
function _temp10(cat) {
  return <div key={cat.title} className="rounded-xl p-4" style={{
    background: cat.bg
  }}><h4 className="text-sm font-bold text-white mb-1">{cat.title}</h4><p className="text-sm italic text-white/80 leading-relaxed">{cat.desc}</p></div>;
}
function MarketBetsStep(t0) {
  const $ = _c(9);
  const {
    answers,
    onRadio
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      title: "Korean Skincare - the AU shelf gap",
      body: "AU buyers are actively seeking Korean skincare partners at sub $30 RRP. Consumer demand is outpacing what's currently ranged - particularly in mass and pharmacy. The founder brand that comes in with the right price architecture and a credible K-beauty story has a genuinely open door right now."
    }, {
      title: "Founder-led, not corporate-led",
      body: "AU buyers are actively seeking founder brands right now - authentic story, engaged community, commercial substance. Founder brands that win aren't just compelling - they're commercially prepared. The story gets you in the room. The commercial plan keeps you on the shelf."
    }, {
      title: "Body as the new face",
      body: "Body care and body makeup are the most under-shelved categories in AU retail relative to consumer demand. Shoppers are there. The innovation is there. The shelf hasn't caught up - and that lag is your opportunity. First mover advantage in body is real, and it's available right now."
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const bets = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">{"Where To Place Bets in AU Retail"}</h2></div><p className="text-gray-600 text-sm">{"Glowpath verdict on the biggest opportunities right now."}</p></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      background: "#e91e8c"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="lg:col-span-2"><p className="text-xs font-bold uppercase tracking-wider mb-2" style={{
        color: "#c5f82a"
      }}>GLOWPATH VERDICT</p><h3 className="text-2xl sm:text-3xl font-black italic text-white leading-tight">{"Where I'd place my bets in AU retail right now."}</h3><p className="text-sm text-white/70 mt-3">{"- Alysha Richards, GLOWPATH"}</p></div>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="rounded-2xl p-6 mb-6" style={t3}><div className="grid grid-cols-1 lg:grid-cols-5 gap-6">{t4}<div className="lg:col-span-3 space-y-4">{bets.map(_temp11)}</div></div></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [{
      value: "directly-relevant",
      label: "My brand is directly in one of these spaces - this validates my timing"
    }, {
      value: "adjacent",
      label: "My brand is adjacent to these opportunities - I can see how to position"
    }, {
      value: "different-space",
      label: "My brand is in a different space entirely"
    }, {
      value: "need-to-pivot",
      label: "This makes me think I need to reposition or sharpen my category argument"
    }, {
      value: "not-sure",
      label: "Not sure yet - I haven't assessed my market positioning"
    }];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== answers || $[7] !== onRadio) {
    t7 = <div>{t2}{t5}<PromptSection title="How relevant are these market opportunities to your brand?"><div className="space-y-2">{t6.map(option => <label key={option.value} className={cn("flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all", answers.marketBetsRelevance === option.value ? "border-indigo-300 bg-indigo-50/70 shadow-sm" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50")}><input type="radio" name="marketBetsRelevance" value={option.value} checked={answers.marketBetsRelevance === option.value} onChange={() => onRadio("marketBetsRelevance", option.value)} className="w-4 h-4 min-w-4 min-h-4 shrink-0 mt-0.5 text-indigo-600 border-gray-300 focus:ring-indigo-500" /><span className="text-sm text-gray-700 leading-relaxed">{option.label}</span></label>)}</div></PromptSection></div>;
    $[6] = answers;
    $[7] = onRadio;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}

// --- Step 10: The Diagnostic - Score Yourself ---
function _temp11(bet) {
  return <div key={bet.title}><div className="h-0.5 mb-3" style={{
      background: "#c5f82a"
    }} /><h4 className="text-sm font-bold text-white mb-1">{bet.title}</h4><p className="text-sm text-white/80 leading-relaxed">{bet.body}</p></div>;
}
function DiagnosticStep(t0) {
  const $ = _c(16);
  const {
    answers,
    onRadio
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== answers || $[1] !== onRadio) {
    const pillars = [{
      key: "pillarCategoryLogic" as keyof Answers,
      title: "CATEGORY LOGIC",
      desc: "Can you articulate - in one sentence - how your brand grows the category, not just your brand?"
    }, {
      key: "pillarCommercialProof" as keyof Answers,
      title: "COMMERCIAL PROOF",
      desc: "Do you have DTC sell-through data, repeat purchase rates, or proven retail performance to share?"
    }, {
      key: "pillarPitchPrecision" as keyof Answers,
      title: "PITCH PRECISION",
      desc: "Is your pitch deck retailer-specific, under 20 slides, and commercial-first?"
    }, {
      key: "pillarMarginArchitecture" as keyof Answers,
      title: "MARGIN ARCHITECTURE",
      desc: "Do you know your sell price, cost, and margin position for this specific channel before you walk in?"
    }, {
      key: "pillarOperationalReadiness" as keyof Answers,
      title: "OPERATIONAL READINESS",
      desc: "Can you confirm stock availability, lead times, and fulfilment capacity for a retail launch?"
    }];
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="mb-6"><div className="flex items-center gap-2.5 mb-2"><h2 className="text-xl font-bold text-gray-900">The Diagnostic - Score Yourself 1-5</h2></div><p className="text-gray-600 text-sm">Rate yourself honestly across the 5 retail readiness pillars.</p></div>;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        background: "#e91e8c"
      };
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="rounded-xl p-4 mb-8" style={t5}><div className="flex gap-3"><AlertCircle className="w-5 h-5 text-white shrink-0 mt-0.5" /><p className="text-sm text-white font-semibold leading-relaxed">Score under 3 in any pillar? You are not ready to pitch.</p></div></div>;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t1 = "space-y-6";
    t2 = pillars.map(pillar => <PromptSection key={pillar.key} title={pillar.title} description={pillar.desc}><div className="flex flex-wrap gap-2">{["1", "2", "3", "4", "5"].map(val => <label key={val} className={cn("flex items-center justify-center w-12 h-12 rounded-xl border cursor-pointer transition-all text-lg font-bold", answers[pillar.key] === val ? Number(val) >= 3 ? "border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm" : "text-white shadow-sm" : "border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-white")} style={answers[pillar.key] === val && Number(val) < 3 ? {
          background: "#e91e8c",
          borderColor: "#e91e8c"
        } : undefined}><input type="radio" name={pillar.key} value={val} checked={answers[pillar.key] === val} onChange={() => onRadio(pillar.key, val)} className="sr-only" />{val}</label>)}</div></PromptSection>);
    $[0] = answers;
    $[1] = onRadio;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2) {
    t5 = <div className={t1}>{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <div>{t3}{t4}{t5}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}

// --- Results View ---

function ResultView(t0) {
  const $ = _c(65);
  const {
    answers,
    onReset,
    onBack
  } = t0;
  const t1 = Number(answers.pillarCategoryLogic ?? 0);
  let pillarScores;
  let t2;
  if ($[0] !== answers.pillarCommercialProof || $[1] !== answers.pillarMarginArchitecture || $[2] !== answers.pillarOperationalReadiness || $[3] !== answers.pillarPitchPrecision || $[4] !== t1) {
    pillarScores = [{
      key: "Category Logic",
      score: t1
    }, {
      key: "Commercial Proof",
      score: Number(answers.pillarCommercialProof ?? 0)
    }, {
      key: "Pitch Precision",
      score: Number(answers.pillarPitchPrecision ?? 0)
    }, {
      key: "Margin Architecture",
      score: Number(answers.pillarMarginArchitecture ?? 0)
    }, {
      key: "Operational Readiness",
      score: Number(answers.pillarOperationalReadiness ?? 0)
    }];
    t2 = pillarScores.reduce(_temp12, 0);
    $[0] = answers.pillarCommercialProof;
    $[1] = answers.pillarMarginArchitecture;
    $[2] = answers.pillarOperationalReadiness;
    $[3] = answers.pillarPitchPrecision;
    $[4] = t1;
    $[5] = pillarScores;
    $[6] = t2;
  } else {
    pillarScores = $[5];
    t2 = $[6];
  }
  const averageScore = t2 / pillarScores.length;
  let t3;
  if ($[7] !== pillarScores) {
    t3 = pillarScores.filter(_temp13);
    $[7] = pillarScores;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const weakPillars = t3;
  pillarScores.filter(_temp14);
  let readinessLevel;
  if (averageScore >= 4) {
    readinessLevel = "retail-ready";
  } else {
    if (averageScore >= 3) {
      readinessLevel = "almost-ready";
    } else {
      if (averageScore >= 2) {
        readinessLevel = "getting-ready";
      } else {
        readinessLevel = "not-ready";
      }
    }
  }
  if (weakPillars.length > 0 && readinessLevel === "retail-ready") {
    readinessLevel = "almost-ready";
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      label: "Not Yet Ready",
      sublabel: "Build your proof stack before approaching retail buyers",
      gradient: "from-red-500 to-rose-600",
      bgStyle: {
        background: "linear-gradient(135deg, #7f1d1d, #e91e8c)"
      }
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      label: "Getting Ready",
      sublabel: "Address weak pillars and start building proof",
      gradient: "from-amber-500 to-orange-600",
      bgStyle: {
        background: "linear-gradient(135deg, #2d0b5a, #e91e8c)"
      }
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      label: "Almost Ready",
      sublabel: "Refine weak areas and prepare retailer-specific materials",
      gradient: "from-blue-500 to-indigo-600",
      bgStyle: {
        background: "linear-gradient(135deg, #1a0a4e, #2d0b5a)"
      }
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      "not-ready": t4,
      "getting-ready": t5,
      "almost-ready": t6,
      "retail-ready": {
        label: "Retail Ready",
        sublabel: "You have strong foundations - target buyer meetings with confidence",
        gradient: "from-emerald-500 to-teal-600",
        bgStyle: {
          background: "linear-gradient(135deg, #0f3d3d, #0f4e4e)"
        }
      }
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const levelConfig = t7;
  const config = levelConfig[readinessLevel];
  let recommendations;
  if ($[13] !== answers.declineRisks || $[14] !== answers.pitchDeckStatus || $[15] !== answers.proofPoints || $[16] !== weakPillars) {
    recommendations = [];
    if (answers.declineRisks?.includes("limited-awareness")) {
      recommendations.push("Build brand awareness before approaching retail. Invest in community, sampling, PR, and social proof so buyers see demand signals before you pitch.");
    }
    if (answers.declineRisks?.includes("saturated-category")) {
      recommendations.push("In a saturated category, your white space argument must be razor sharp. Map exactly where you sit vs. what is already on shelf and define the incremental customer you bring.");
    }
    if (answers.declineRisks?.includes("unclear-positioning")) {
      recommendations.push("Clarify your brand positioning before you pitch. A buyer needs to understand in 10 seconds why your brand exists and who it is for.");
    }
    if (answers.declineRisks?.includes("weak-pitch")) {
      recommendations.push("Rebuild your pitch deck with the 5-part architecture: Category Case, Proof Stack, Retailer Fit, Commercial Terms, Launch Plan. Lead with commercial story, not brand story.");
    }
    if (answers.declineRisks?.includes("tight-margins")) {
      recommendations.push("Resolve your margin structure before pitching. Know your sell price, cost, and margin position for the specific channel. Consider whether your pricing model supports promotional depth.");
    }
    if (answers.declineRisks?.includes("operational-concerns")) {
      recommendations.push("Shore up operational readiness: confirm production capacity, lead times, distribution, and compliance requirements. These are solvable problems - solve them before you pitch.");
    }
    const proofPoints = answers.proofPoints ?? [];
    if (!proofPoints.includes("dtc-sales") && !proofPoints.includes("retail-performance")) {
      recommendations.push("You lack velocity proof (DTC or retail sales data). Focus on building measurable sales evidence through your own channels before approaching retail buyers.");
    }
    if (!proofPoints.includes("community") && !proofPoints.includes("social")) {
      recommendations.push("Build community engagement and social proof. Buyers want to see that people will pull product off the shelf from day one. UGC, reviews, and active engagement matter more than follower count.");
    }
    if (answers.pitchDeckStatus === "none") {
      recommendations.push("Create a retailer-specific pitch deck. Use the 5-part architecture and keep it under 20 slides. Lead with category growth, not your founder story.");
    } else {
      if (answers.pitchDeckStatus === "generic") {
        recommendations.push("Customise your pitch for each retailer. A generic deck signals you have not done the work. Show you understand their shopper, their price architecture, and their category strategy.");
      } else {
        if (answers.pitchDeckStatus === "brand-story") {
          recommendations.push("Restructure your pitch to lead with category growth and commercial opportunity. Your brand story can support the pitch, but it should not lead it.");
        }
      }
    }
    for (const pillar of weakPillars) {
      recommendations.push(`Your ${pillar.key} score is ${pillar.score}/5. This pillar needs focused attention before you approach buyers.`);
    }
    $[13] = answers.declineRisks;
    $[14] = answers.pitchDeckStatus;
    $[15] = answers.proofPoints;
    $[16] = weakPillars;
    $[17] = recommendations;
  } else {
    recommendations = $[17];
  }
  let nextSteps;
  if ($[18] !== readinessLevel) {
    nextSteps = [];
    if (readinessLevel === "not-ready") {
      nextSteps.push("Focus on building DTC sales data and community engagement as your proof foundation");
      nextSteps.push("Research your target retailers - map the competitive set and identify your white space");
      nextSteps.push("Resolve operational basics: production capacity, packaging compliance, insurance");
      nextSteps.push("Consider independent/specialty retail as your first retail proof point");
    } else {
      if (readinessLevel === "getting-ready") {
        nextSteps.push("Address your weakest pillar (highlighted above) before any buyer approach");
        nextSteps.push("Build your retailer-specific pitch using the 5-part architecture");
        nextSteps.push("Start with smaller/independent retailers to build proven retail performance");
        nextSteps.push("Run sampling and activation campaigns to build consumer data you can present");
      } else {
        if (readinessLevel === "almost-ready") {
          nextSteps.push("Refine your pitch deck: ensure it is retailer-specific, under 20 slides, and commercial-first");
          nextSteps.push("Prepare channel-specific commercial terms including margin, promo depth, and launch investment");
          nextSteps.push("Research specific buyer contacts and appropriate timing (range reviews, trade shows)");
          nextSteps.push("Plan your launch activation: what you will invest to make it successful for the retailer");
        } else {
          nextSteps.push("Target specific buyers during range review windows with your retailer-specific pitch");
          nextSteps.push("Have your commercial terms, launch plan, and promotional calendar ready for the meeting");
          nextSteps.push("Plan launch activation: PR, sampling, events, and digital support for the retailer");
          nextSteps.push("Ensure your supply chain can scale to meet demand from day one");
        }
      }
    }
    $[18] = readinessLevel;
    $[19] = nextSteps;
  } else {
    nextSteps = $[19];
  }
  const t8 = config.bgStyle;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="text-sm font-medium uppercase tracking-wider mb-2" style={{
      color: "#c5f82a"
    }}>Your Retail Readiness Assessment</p>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== averageScore) {
    t10 = averageScore.toFixed(1);
    $[21] = averageScore;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <div className="text-6xl font-black mb-2">{t10}/5</div>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== config.label) {
    t12 = <div className="inline-block px-4 py-1.5 bg-white/20 rounded-full text-sm font-semibold backdrop-blur-sm">{config.label}</div>;
    $[25] = config.label;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== config.sublabel) {
    t13 = <p className="text-sm text-white/70 mt-3">{config.sublabel}</p>;
    $[27] = config.sublabel;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t12 || $[31] !== t13) {
    t14 = <div className="text-center">{t9}{t11}{t12}{t13}</div>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== config.bgStyle || $[34] !== t14) {
    t15 = <div className="rounded-2xl p-8 text-white" style={t8}>{t14}</div>;
    $[33] = config.bgStyle;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <h3 className="text-lg font-bold text-gray-900 mb-1">Score By Pillar</h3>;
    t17 = <p className="text-sm text-gray-500 mb-5">Score below 3 in any pillar? You are not ready to pitch in that area.</p>;
    $[36] = t16;
    $[37] = t17;
  } else {
    t16 = $[36];
    t17 = $[37];
  }
  let t18;
  if ($[38] !== pillarScores) {
    t18 = <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">{t16}{t17}<div className="space-y-5">{pillarScores.map(_temp15)}</div></div>;
    $[38] = pillarScores;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== weakPillars) {
    t19 = weakPillars.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 sm:p-8"><h3 className="text-lg font-bold text-amber-900 mb-1">Critical Gaps</h3><p className="text-sm text-amber-700 mb-4">These pillars scored below 3 and need focused attention before you pitch.</p><div className="space-y-2">{weakPillars.map(_temp16)}</div></div>;
    $[40] = weakPillars;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== recommendations) {
    t20 = recommendations.length > 0 && <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8"><h3 className="text-lg font-bold text-gray-900 mb-1">Personalised Recommendations</h3><p className="text-sm text-gray-500 mb-4">Based on your responses across all steps.</p><ul className="space-y-3">{recommendations.slice(0, 8).map(_temp17)}</ul></div>;
    $[42] = recommendations;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  let t22;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <h3 className="text-lg font-bold text-gray-900 mb-1">Your Next Steps</h3>;
    t22 = <p className="text-sm text-gray-500 mb-4">Prioritised actions for your readiness level.</p>;
    $[44] = t21;
    $[45] = t22;
  } else {
    t21 = $[44];
    t22 = $[45];
  }
  let t23;
  if ($[46] !== nextSteps) {
    t23 = <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">{t21}{t22}<ol className="space-y-3">{nextSteps.map(_temp18)}</ol></div>;
    $[46] = nextSteps;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== answers) {
    t24 = <AssessmentEnquiryBlock answers={answers} />;
    $[48] = answers;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== onBack) {
    t25 = <button onClick={onBack} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg cursor-pointer">Review Answers</button>;
    $[50] = onBack;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== onReset) {
    t26 = <button onClick={onReset} className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg text-sm font-medium hover:from-indigo-700 hover:to-violet-700 shadow-sm cursor-pointer">Start Over</button>;
    $[52] = onReset;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t25 || $[55] !== t26) {
    t27 = <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100 rounded-2xl p-6 text-center"><div className="flex items-center justify-center gap-3">{t25}{t26}</div></div>;
    $[54] = t25;
    $[55] = t26;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t15 || $[58] !== t18 || $[59] !== t19 || $[60] !== t20 || $[61] !== t23 || $[62] !== t24 || $[63] !== t27) {
    t28 = <div className="max-w-4xl mx-auto space-y-6">{t15}{t18}{t19}{t20}{t23}{t24}{t27}</div>;
    $[57] = t15;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = t23;
    $[62] = t24;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  return t28;
}

// --- Assessment Enquiry Block ---
function _temp18(step, i_0) {
  return <li key={i_0} className="flex gap-3 text-sm text-gray-700"><span className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-indigo-500 to-violet-600 text-white rounded-full flex items-center justify-center text-xs font-bold">{i_0 + 1}</span><span className="leading-relaxed">{step}</span></li>;
}
function _temp17(rec, i) {
  return <li key={i} className="flex gap-3 text-sm text-gray-700"><Lightbulb className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" /><span className="leading-relaxed">{rec}</span></li>;
}
function _temp16(wp) {
  return <div key={wp.key} className="flex items-center gap-3 bg-white/70 rounded-xl p-3 border border-amber-200"><AlertCircle className="w-5 h-5 text-white shrink-0" /><div><span className="text-sm font-semibold text-gray-900">{wp.key}</span><span className="text-sm text-amber-700 ml-2">- scored {wp.score}/5</span></div></div>;
}
function _temp15(ps) {
  const pct = ps.score / 5 * 100;
  const isWeak = ps.score < 3;
  return <div key={ps.key}><div className="flex items-center justify-between mb-1.5"><div className="flex items-center gap-2"><span className="text-sm font-semibold text-gray-800">{ps.key}</span>{isWeak && <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Needs Work</span>}</div><span className={cn("text-sm font-bold", ps.score >= 4 ? "text-emerald-600" : ps.score >= 3 ? "text-blue-600" : "text-amber-600")}>{ps.score}/5</span></div><div className="h-3 bg-gray-100 rounded-full overflow-hidden"><div className={cn("h-full rounded-full transition-all duration-700", ps.score >= 4 ? "bg-gradient-to-r from-emerald-400 to-emerald-500" : ps.score >= 3 ? "bg-gradient-to-r from-blue-400 to-indigo-500" : "bg-gradient-to-r from-amber-400 to-orange-500")} style={{
        width: `${pct}%`
      }} /></div></div>;
}
function _temp14(p_1) {
  return p_1.score >= 4;
}
function _temp13(p_0) {
  return p_0.score < 3;
}
function _temp12(sum, p) {
  return sum + p.score;
}
function AssessmentEnquiryBlock(t0) {
  const $ = _c(51);
  const {
    answers
  } = t0;
  const {
    data: session
  } = useSession();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "",
      email: "",
      phone: "",
      message: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [formData, setFormData] = useState(t1);
  const [showSuccess, setShowSuccess] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mutationFn: _temp19,
      onSuccess: () => {
        setShowSuccess(true);
        toast.success("Your request has been sent successfully");
      },
      onError: _temp20
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sendEnquiry = useMutation(t2);
  let t3;
  if ($[2] !== answers || $[3] !== formData.email || $[4] !== formData.message || $[5] !== formData.name || $[6] !== formData.phone || $[7] !== sendEnquiry || $[8] !== session?.user?.email || $[9] !== session?.user?.name) {
    t3 = function handleSubmit(e) {
      e.preventDefault();
      setShowSuccess(false);
      const name = formData.name || session?.user?.name || "";
      const email = formData.email || session?.user?.email || "";
      if (!name || !email) {
        toast.error("Please fill in your name and email");
        return;
      }
      sendEnquiry.mutate({
        senderName: name,
        senderEmail: email,
        senderPhone: formData.phone || undefined,
        message: formData.message || "I would like a free 30 minute retail readiness assessment consultation.",
        toolId: "glowpath-au-retail-readiness",
        data: answers
      });
    };
    $[2] = answers;
    $[3] = formData.email;
    $[4] = formData.message;
    $[5] = formData.name;
    $[6] = formData.phone;
    $[7] = sendEnquiry;
    $[8] = session?.user?.email;
    $[9] = session?.user?.name;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleSubmit = t3;
  const nameValue = formData.name || session?.user?.name || "";
  const emailValue = formData.email || session?.user?.email || "";
  if (showSuccess) {
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="rounded-2xl p-6 sm:p-8 border border-emerald-200 bg-emerald-50"><div className="flex items-center gap-3 mb-3"><CheckCircle2 className="w-6 h-6 text-emerald-600" /><h3 className="text-lg font-bold text-emerald-900">Request Sent</h3></div><p className="text-sm text-emerald-700">Your assessment and contact details have been sent to the Glowpath team. They will be in touch within 1-2 business days to schedule your free 30 minute consultation.</p></div>;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  let t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      background: "#e91e8c"
    };
    t5 = <h3 className="text-lg sm:text-xl font-black text-white mb-2">Not sure you're ready? Get expert guidance.</h3>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="p-6 sm:p-8" style={t4}>{t5}<p className="text-sm text-white/90 leading-relaxed">If you scored below 3 in any pillar, or simply want a second opinion before approaching buyers, the Glowpath team offers a{" "}<strong className="text-white">free 30 minute retail readiness assessment</strong> where they review your results and give you a clear action plan.</p><p className="text-sm text-white/70 mt-2 italic">Your assessment answers will be shared so they can prepare tailored advice for your brand.</p></div>;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <label htmlFor="enquiry-name" className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_0 => setFormData(prev => ({
      ...prev,
      name: e_0.target.value
    }));
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== nameValue) {
    t9 = <div>{t7}<input id="enquiry-name" type="text" required={true} value={nameValue} onChange={t8} placeholder="Jane Smith" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" /></div>;
    $[17] = nameValue;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <label htmlFor="enquiry-email" className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_1 => setFormData(prev_0 => ({
      ...prev_0,
      email: e_1.target.value
    }));
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== emailValue) {
    t12 = <div>{t10}<input id="enquiry-email" type="email" required={true} value={emailValue} onChange={t11} placeholder="jane@yourbrand.com" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" /></div>;
    $[21] = emailValue;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t9) {
    t13 = <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{t9}{t12}</div>;
    $[23] = t12;
    $[24] = t9;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <label htmlFor="enquiry-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e_2 => setFormData(prev_1 => ({
      ...prev_1,
      phone: e_2.target.value
    }));
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== formData.phone) {
    t16 = <div>{t14}<input id="enquiry-phone" type="tel" value={formData.phone} onChange={t15} placeholder="+61 400 123 456" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" /></div>;
    $[28] = formData.phone;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <label htmlFor="enquiry-message" className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = e_3 => setFormData(prev_2 => ({
      ...prev_2,
      message: e_3.target.value
    }));
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== formData.message) {
    t19 = <div>{t17}<textarea id="enquiry-message" value={formData.message} onChange={t18} placeholder="Tell us about your brand and what you'd like help with..." rows={3} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none" /></div>;
    $[32] = formData.message;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = sendEnquiry.isPending;
  const t21 = sendEnquiry.isPending ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "text-white hover:opacity-90 shadow-sm";
  let t22;
  if ($[34] !== t21) {
    t22 = cn("w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer", t21);
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== sendEnquiry.isPending) {
    t23 = !sendEnquiry.isPending ? {
      background: "#e91e8c"
    } : undefined;
    $[36] = sendEnquiry.isPending;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Send className="w-4 h-4" />;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  const t25 = sendEnquiry.isPending ? "Sending..." : "Request Free Assessment";
  let t26;
  if ($[39] !== sendEnquiry.isPending || $[40] !== t22 || $[41] !== t23 || $[42] !== t25) {
    t26 = <button type="submit" disabled={t20} className={t22} style={t23}>{t24}{t25}</button>;
    $[39] = sendEnquiry.isPending;
    $[40] = t22;
    $[41] = t23;
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <p className="text-xs text-gray-400 text-center">Your assessment results will be attached so Glowpath can prepare tailored advice.</p>;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== handleSubmit || $[46] !== t13 || $[47] !== t16 || $[48] !== t19 || $[49] !== t26) {
    t28 = <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">{t6}<form onSubmit={handleSubmit} className="p-6 sm:p-8 bg-white space-y-4">{t13}{t16}{t19}{t26}{t27}</form></div>;
    $[45] = handleSubmit;
    $[46] = t13;
    $[47] = t16;
    $[48] = t19;
    $[49] = t26;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  return t28;
}
function _temp20() {
  toast.error("Failed to send your request. Please try again.");
}
function _temp19(payload) {
  return authenticatedApiClient.post("tools/tool-enquiry", {
    json: payload
  }).json();
}